import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
    Button, TextField, Box, Grid, Paper, IconButton,
    Typography, Stack, Chip, Divider, alpha, useTheme, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AgentSearch from '../../common/AgentSearch';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAgentsAsync, selectAgents } from '../../app/services/agents/agentSlice';
import { createCashMemoAsync, RefillEntry } from '../../app/services/cashmemo/cashMemoSlice';
import { AppDispatch } from '../../app/store';

const emptyEntry = (): RefillEntry => ({
    memoNumber: uuidv4(),
    agentName: '',
    totalRefillDelivered: 0,
    cdcmsCashMemoPunch: 0,
    vitaranCashMemoPunch: 0,
    totalCashMemoPunch: 0,
    remainingCashMemo: 0,
    remarks: '',
});

const CashMemoPunchForm = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { agents: agentList, status } = useSelector(selectAgents);
    const theme = useTheme();

    const [entries, setEntries] = useState<RefillEntry[]>([emptyEntry()]);

    const handleAddRow = () => setEntries(prev => [...prev, emptyEntry()]);

    const handleDeleteRow = (memoNumber: string) => {
        if (entries.length === 1) return;
        setEntries(entries.filter(e => e.memoNumber !== memoNumber));
    };

    const handleAgentSelect = (memoNumber: string, agent: any) => {
        const agentName = agent ? (typeof agent === 'string' ? agent : agent.label) : '';
        const refillCount = agent && typeof agent !== 'string' ? agent.refillCount : 0;

        setEntries(prev =>
            prev.map(entry => {
                if (entry.memoNumber !== memoNumber) return entry;
                const cdcms = entry.cdcmsCashMemoPunch || 0;
                const vitaran = entry.vitaranCashMemoPunch || 0;
                const totalCashMemoPunch = cdcms + vitaran;
                return {
                    ...entry,
                    agentName,
                    totalRefillDelivered: refillCount,
                    totalCashMemoPunch,
                    remainingCashMemo: refillCount - totalCashMemoPunch,
                };
            })
        );
    };

    const handleChange = (memoNumber: string, field: keyof RefillEntry, value: string | number) => {
        setEntries(prev =>
            prev.map(entry => {
                if (entry.memoNumber !== memoNumber) return entry;
                const updated = { ...entry, [field]: value };
                updated.totalCashMemoPunch = (updated.cdcmsCashMemoPunch || 0) + (updated.vitaranCashMemoPunch || 0);
                updated.remainingCashMemo = (updated.totalRefillDelivered || 0) - updated.totalCashMemoPunch;
                return updated;
            })
        );
    };

    const handleSave = async () => {
        for (const entry of entries) {
            if (!entry.agentName) {
                toast.error('Agent name is required for all entries.');
                return;
            }
        }
        try {
            for (const entry of entries) {
                const resultAction = await dispatch(createCashMemoAsync(entry));
                if (createCashMemoAsync.rejected.match(resultAction)) {
                    toast.error(
                        (resultAction.payload as string) || `Failed to save cash memo for ${entry.agentName}.`
                    );
                    return;
                }
            }
            toast.success('All Cash Memo entries saved successfully!');
            setEntries([emptyEntry()]);
        } catch (error: any) {
            toast.error(error.message || 'An error occurred while saving.');
        }
    };

    React.useEffect(() => {
        if (status === 'idle') dispatch(fetchAgentsAsync());
    }, [dispatch, status]);

    const getRemainingColor = (remaining: number) => {
        if (remaining < 0) return 'error';
        if (remaining === 0) return 'success';
        return 'warning';
    };

    return (
        <Box>
            {/* Section label */}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                {entries.length} {entries.length === 1 ? 'Entry' : 'Entries'}
            </Typography>

            <Stack spacing={2}>
                {entries.map((entry, idx) => (
                    <Paper
                        key={entry.memoNumber}
                        elevation={0}
                        sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            overflow: 'hidden',
                            transition: 'box-shadow 0.2s',
                            '&:hover': {
                                boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.1)}`,
                                borderColor: alpha(theme.palette.primary.main, 0.3),
                            },
                        }}
                    >
                        {/* Entry header */}
                        <Box
                            sx={{
                                px: 2,
                                py: 1,
                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <PersonOutlineIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                <Typography variant="caption" fontWeight={600} color="primary.main">
                                    Entry #{idx + 1}
                                </Typography>
                                {entry.agentName && (
                                    <Chip
                                        label={entry.agentName}
                                        size="small"
                                        sx={{
                                            height: 20,
                                            fontSize: '0.65rem',
                                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                                            color: 'primary.dark',
                                            fontWeight: 600,
                                        }}
                                    />
                                )}
                            </Stack>
                            <Tooltip title={entries.length === 1 ? 'At least one entry required' : 'Remove entry'}>
                                <span>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        disabled={entries.length === 1}
                                        onClick={() => handleDeleteRow(entry.memoNumber)}
                                        sx={{
                                            '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) },
                                        }}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>

                        {/* Entry fields */}
                        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                                {/* Agent */}
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                        Agent Name *
                                    </Typography>
                                    <AgentSearch
                                        onSelectAgent={(agent) => handleAgentSelect(entry.memoNumber, agent)}
                                        list={agentList.map(agent => ({ label: agent.name, value: agent.name, ...agent }))}
                                        loading={status === 'loading'}
                                    />
                                </Grid>

                                {/* Refill Delivered */}
                                <Grid item xs={6} sm={3} md={2}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                        Refill Delivered
                                    </Typography>
                                    <TextField
                                        type="number"
                                        value={entry.totalRefillDelivered}
                                        onChange={(e) => handleChange(entry.memoNumber, 'totalRefillDelivered', Number(e.target.value))}
                                        fullWidth
                                        size="small"
                                        inputProps={{ min: 0 }}
                                    />
                                </Grid>

                                {/* CDCMS */}
                                <Grid item xs={6} sm={3} md={2}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                        CDCMS Punch
                                    </Typography>
                                    <TextField
                                        type="number"
                                        value={entry.cdcmsCashMemoPunch}
                                        onChange={(e) => handleChange(entry.memoNumber, 'cdcmsCashMemoPunch', Number(e.target.value))}
                                        fullWidth
                                        size="small"
                                        inputProps={{ min: 0 }}
                                    />
                                </Grid>

                                {/* Vitaran */}
                                <Grid item xs={6} sm={3} md={2}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                        Vitaran Punch
                                    </Typography>
                                    <TextField
                                        type="number"
                                        value={entry.vitaranCashMemoPunch}
                                        onChange={(e) => handleChange(entry.memoNumber, 'vitaranCashMemoPunch', Number(e.target.value))}
                                        fullWidth
                                        size="small"
                                        inputProps={{ min: 0 }}
                                    />
                                </Grid>

                                {/* Computed: Total Punch */}
                                <Grid item xs={6} sm={3} md={1}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                        Total Punch
                                    </Typography>
                                    <Box
                                        sx={{
                                            height: 40,
                                            px: 1.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            bgcolor: alpha(theme.palette.info.main, 0.08),
                                            borderRadius: 1,
                                            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                                        }}
                                    >
                                        <Typography variant="body2" fontWeight={700} color="info.dark">
                                            {entry.totalCashMemoPunch}
                                        </Typography>
                                    </Box>
                                </Grid>

                                {/* Computed: Remaining */}
                                <Grid item xs={6} sm={3} md={1}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                        Remaining
                                    </Typography>
                                    <Box
                                        sx={{
                                            height: 40,
                                            px: 1.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            bgcolor: alpha(theme.palette[getRemainingColor(entry.remainingCashMemo)].main, 0.08),
                                            borderRadius: 1,
                                            border: `1px solid ${alpha(theme.palette[getRemainingColor(entry.remainingCashMemo)].main, 0.2)}`,
                                        }}
                                    >
                                        <Typography variant="body2" fontWeight={700} color={`${getRemainingColor(entry.remainingCashMemo)}.dark`}>
                                            {entry.remainingCashMemo}
                                        </Typography>
                                    </Box>
                                </Grid>

                                {/* Remarks */}
                                <Grid item xs={12} sm={9} md>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                        Remarks
                                    </Typography>
                                    <TextField
                                        placeholder="Optional remarks..."
                                        value={entry.remarks}
                                        onChange={(e) => handleChange(entry.memoNumber, 'remarks', e.target.value)}
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Paper>
                ))}
            </Stack>

            {/* Action Buttons */}
            <Divider sx={{ my: 3 }} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddRow}
                    color="primary"
                    sx={{
                        borderRadius: 2,
                        borderWidth: 2,
                        fontWeight: 600,
                        px: 3,
                        '&:hover': { borderWidth: 2 },
                    }}
                >
                    Add Row
                </Button>
                <Button
                    variant="contained"
                    startIcon={<SaveOutlinedIcon />}
                    onClick={handleSave}
                    color="primary"
                    sx={{
                        borderRadius: 2,
                        fontWeight: 600,
                        px: 4,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}`,
                        '&:hover': {
                            boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.5)}`,
                        },
                    }}
                >
                    Save All Entries
                </Button>
            </Stack>
        </Box>
    );
};

export default CashMemoPunchForm;
